import { devLog } from './devLog';
/**
 * ContextManager
 * 
 * Manages the AI context state and provides utilities for parsing context
 * from messages and tracking the current conversation context.
 */

class ContextManager {
  constructor() {
    this.currentContext = {
      items: []
    };
    this.listeners = new Set();
    // Cache for tenant and project names to avoid regex parsing
    this.entityCache = {
      tenants: new Map(), // tenantId -> tenant object
      projects: new Map() // projectId -> project object
    };
  }

  /**
   * Cache entity information from tool results
   * @param {string} entityType - 'tenant' or 'project'
   * @param {string} id - Entity ID
   * @param {Object} data - Entity data
   */
  cacheEntity(entityType, id, data) {
    if (entityType === 'tenant' && id && data) {
      this.entityCache.tenants.set(id, data);
    } else if (entityType === 'project' && id && data) {
      this.entityCache.projects.set(id, data);
    }
  }

  /**
   * Get cached entity by ID
   * @param {string} entityType - 'tenant' or 'project'
   * @param {string} id - Entity ID
   * @returns {Object|null} Cached entity or null
   */
  getCachedEntity(entityType, id) {
    if (entityType === 'tenant') {
      return this.entityCache.tenants.get(id) || null;
    } else if (entityType === 'project') {
      return this.entityCache.projects.get(id) || null;
    }
    return null;
  }

  /**
   * Extract and cache entities from tool results
   * @param {string} toolName - Name of the tool that was called
   * @param {Object} toolResult - Result from the tool
   */
  extractAndCacheEntities(toolName, toolResult) {
    if (!toolResult) return;

    try {
      const resultData = typeof toolResult === 'string' ? JSON.parse(toolResult) : toolResult;
      
      // Cache tenants from list_tenants result
      if (toolName.includes('list_tenants') && Array.isArray(resultData)) {
        resultData.forEach(tenant => {
          if (tenant.id || tenant._id) {
            this.cacheEntity('tenant', tenant.id || tenant._id, tenant);
          }
        });
      }
      
      // Cache projects from list_projects result
      if (toolName.includes('list_projects') && Array.isArray(resultData)) {
        resultData.forEach(project => {
          if (project.id || project._id) {
            this.cacheEntity('project', project.id || project._id, project);
          }
        });
      }
      
      // Cache single project from get_project result
      if (toolName.includes('get_project') && resultData.id) {
        this.cacheEntity('project', resultData.id, resultData);
      }
    } catch (e) {
      // Ignore parsing errors
    }
  }

  /**
   * Subscribe to context changes
   * @param {Function} listener - Callback function to be called when context changes
   * @returns {Function} Unsubscribe function
   */
  subscribe(listener) {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Notify all listeners of context change
   */
  notifyListeners() {
    this.listeners.forEach(listener => {
      listener(this.currentContext);
    });
  }

  /**
   * Update the current context
   * @param {Object} newContext - New context object with items array
   */
  updateContext(newContext) {
    // eslint-disable-next-line no-console
    devLog('🔍 ContextManager: Updating context', newContext);
    this.currentContext = newContext;
    this.notifyListeners();
  }

  /**
   * Clear the current context
   */
  clearContext() {
    this.currentContext = {items: []};
    this.notifyListeners();
  }

  /**
   * Get the current context
   * @returns {Object} Current context
   */
  getContext() {
    return this.currentContext;
  }

  /**
   * Parse context from message text (looking for patterns)
   * @param {string} text - Message text to parse
   * @returns {Object|null} Parsed context or null if no context found
   */
  parseContextFromMessage(text) {
    if (!text) return null;

    const context = {items: []};

    // Look for tool usage patterns and listing operations
    const listTenantsMatch = text.match(/list[_\s]tenants|tenants\s+I\s+found|browsing\s+tenants/i);
    const listProjectsMatch = text.match(/list[_\s]projects|projects\s+I\s+found|browsing\s+projects/i);
    
    // Look for Mia-Platform context patterns with improved regex
    // Matches: "tenant 'demo live'" or "tenant demo live (ID:" or "tenant: demo live"
    const miaTenantMatch = text.match(/\b(?:tenant|Tenant)(?:\s+is)?[:\s]+["']?([a-zA-Z0-9][a-zA-Z0-9\s-_]+?)["']?(?:\s*\(|$|,|\.|:)/i);
    const miaProjectMatch = text.match(/\b(?:project|Project)(?:\s+is)?[:\s]+["']?([a-zA-Z0-9][a-zA-Z0-9\s-_]+?)["']?(?:\s*\(|$|,|\.|:)/i);
    const miaEnvMatch = text.match(/\b(?:environment|Environment|env|Env)[:\s]+["']?([a-zA-Z0-9-_]+)["']?/i);

    // Look for Atlassian context patterns
    const jiraIssueMatch = text.match(/\b([A-Z]+-\d+)\b/); // Issue key pattern (e.g., ABC-123)
    const jiraProjectMatch = text.match(/\b(?:Jira project|JIRA project|project)\s+["']?([A-Z]+)["']?/i);
    const confluenceSpaceMatch = text.match(/\b(?:space|Space)\s+["']?([a-zA-Z0-9-_]+)["']?/i);
    const confluencePageMatch = text.match(/\b(?:page|Page)\s+["']?([^"'\n]+)["']?/i);

    // Check for listing operations (show base platform context)
    if (listTenantsMatch || (text.includes('tenants') && text.includes('found'))) {
      context.items.push({
        label: 'Mia-Platform',
        icon: '🏢',
        tooltip: 'Mia-Platform Console'
      });
      context.items.push({
        label: 'Tenants',
        icon: '🏛️',
        tooltip: 'Browsing tenants'
      });
      return context.items.length > 0 ? context : null;
    }

    if (listProjectsMatch || (text.includes('projects') && text.includes('found'))) {
      context.items.push({
        label: 'Mia-Platform',
        icon: '🏢',
        tooltip: 'Mia-Platform Console'
      });
      
      // Try to extract tenant if mentioned before projects
      if (miaTenantMatch) {
        const tenantName = miaTenantMatch[1].trim();
        // Only add if it's a specific tenant name (not plural)
        if (!tenantName.match(/^tenants?$/i)) {
          context.items.push({
            label: tenantName,
            icon: '🏛️',
            tooltip: `Tenant: ${tenantName}`
          });
        }
      }
      
      // Don't add "Projects" label - just show tenant
      return context.items.length > 0 ? context : null;
    }

    // Build context based on what was found
    if (miaTenantMatch || miaProjectMatch || miaEnvMatch) {
      // Mia-Platform context
      context.items.push({
        label: 'Mia-Platform',
        icon: '🏢',
        tooltip: 'Mia-Platform Console'
      });

      if (miaTenantMatch) {
        const tenantName = miaTenantMatch[1].trim();
        // Only add if it's a specific tenant name (not plural)
        if (!tenantName.match(/^tenants?$/i)) {
          context.items.push({
            label: tenantName,
            icon: '🏛️',
            tooltip: `Tenant: ${tenantName}`
          });
        }
      }

      if (miaProjectMatch) {
        const projectName = miaProjectMatch[1].trim();
        // Only add if it's a specific project name (not plural)
        if (!projectName.match(/^projects?$/i)) {
          context.items.push({
            label: projectName,
            icon: '📦',
            tooltip: `Project: ${projectName}`
          });
        }
      }

      if (miaEnvMatch) {
        context.items.push({
          label: miaEnvMatch[1],
          icon: '🌍',
          tooltip: `Environment: ${miaEnvMatch[1]}`
        });
      }
    } else if (jiraIssueMatch || jiraProjectMatch) {
      // Atlassian issue tracking context
      context.items.push({
        label: 'Atlassian',
        icon: '🔷',
        tooltip: 'Atlassian Issue Tracker'
      });

      if (jiraProjectMatch) {
        context.items.push({
          label: jiraProjectMatch[1],
          icon: '📋',
          tooltip: `Project: ${jiraProjectMatch[1]}`
        });
      }

      if (jiraIssueMatch) {
        context.items.push({
          label: jiraIssueMatch[1],
          icon: '🎫',
          tooltip: `Issue: ${jiraIssueMatch[1]}`
        });
      }
    } else if (confluenceSpaceMatch || confluencePageMatch) {
      // Atlassian Confluence context
      context.items.push({
        label: 'Confluence',
        icon: '📚',
        tooltip: 'Atlassian Confluence'
      });

      if (confluenceSpaceMatch) {
        context.items.push({
          label: confluenceSpaceMatch[1],
          icon: '📁',
          tooltip: `Space: ${confluenceSpaceMatch[1]}`
        });
      }

      if (confluencePageMatch) {
        const pageName = confluencePageMatch[1].substring(0, 30);
        context.items.push({
          label: pageName,
          icon: '📄',
          tooltip: `Page: ${confluencePageMatch[1]}`
        });
      }
    }

    return context.items.length > 0 ? context : null;
  }

  /**
   * Extract context from structured content (inline UI)
   * @param {Object} structuredContent - Structured content object
   * @param {string} contentType - Type of structured content
   * @returns {Object|null} Parsed context or null
   */
  extractContextFromStructuredContent(structuredContent, contentType) {
    if (!structuredContent) return null;

    const context = {items: []};

    if (contentType === 'project-details') {
      // Extract from project details
      context.items.push({
        label: 'Mia-Platform',
        icon: '🏢',
        tooltip: 'Mia-Platform Console'
      });

      // Try to extract project name from repository URL or other fields
      if (structuredContent.repositoryURL) {
        const repoMatch = structuredContent.repositoryURL.match(/\/([^/]+)$/);
        if (repoMatch) {
          context.items.push({
            label: repoMatch[1],
            icon: '📦',
            tooltip: 'Project'
          });
        }
      }

      // Add environment if there's only one production environment
      const prodEnvs = (structuredContent.environments || []).filter(env => env.isProduction);
      if (prodEnvs.length === 1) {
        context.items.push({
          label: prodEnvs[0].label,
          icon: '🌍',
          tooltip: `Environment: ${prodEnvs[0].label}`
        });
      }
    }

    return context.items.length > 0 ? context : null;
  }

  /**
   * Update context based on the latest message
   * @param {Object} message - Message object with text and optional structured content
   */
  updateContextFromMessage(message) {
    // eslint-disable-next-line no-console
    devLog('🔍 ContextManager: updateContextFromMessage called with', message);
    
    if (!message || message.type !== 'received' || message.isIntermediate) {
      // eslint-disable-next-line no-console
      devLog('🔍 ContextManager: Skipping - not a finalized received message');
      return;
    }

    // Try to extract context from structured content first
    if (message.structuredContent && message.contentType) {
      const structuredContext = this.extractContextFromStructuredContent(
        message.structuredContent,
        message.contentType
      );
      if (structuredContext) {
        // eslint-disable-next-line no-console
        devLog('🔍 ContextManager: Found context from structured content', structuredContext);
        this.updateContext(structuredContext);
        return;
      }
    }

    // Check for reasoning steps that might indicate tool usage
    if (message.reasoningSteps && message.reasoningSteps.length > 0) {
      // eslint-disable-next-line no-console
      devLog('🔍 ContextManager: Checking reasoning steps', message.reasoningSteps);
      
      for (const step of message.reasoningSteps) {
        // Check if a tool was used that gives us context
        if (step.functionCall) {
          const toolName = step.functionCall.name;
          const toolArgs = step.functionCall.arguments || {};
          const toolResult = step.functionCallResult;
          // eslint-disable-next-line no-console
          devLog('🔍 ContextManager: Found tool usage', toolName, 'with args', toolArgs, 'result', toolResult);
          
          // Mia-Platform tools - extract tenant/project from arguments
          if (toolName && toolName.includes('list_tenants')) {
            // Cache tenants from result
            this.extractAndCacheEntities(toolName, toolResult);
            
            // eslint-disable-next-line no-console
            devLog('🔍 ContextManager: Setting context for list_tenants');
            this.updateContext({
              items: [
                {label: 'Mia-Platform', icon: '🏢', tooltip: 'Mia-Platform Console'},
                {label: 'Tenants', icon: '🏛️', tooltip: 'Browsing tenants'}
              ]
            });
            return;
          }
          
          if (toolName && toolName.includes('list_projects')) {
            // First, extract and cache entities from tool result
            this.extractAndCacheEntities(toolName, toolResult);
            
            const contextItems = [{label: 'Mia-Platform', icon: '🏢', tooltip: 'Mia-Platform Console'}];
            
            // Extract tenant ID from arguments
            const tenantIds = toolArgs.tenantIds || toolArgs.tenant_ids;
            const tenantId = Array.isArray(tenantIds) ? tenantIds[0] : tenantIds;
            
            if (tenantId) {
              // Try to get tenant name from cache
              const cachedTenant = this.getCachedEntity('tenant', tenantId);
              const tenantName = cachedTenant?.name || tenantId;
              
              contextItems.push({
                label: tenantName,
                icon: '🏛️',
                tooltip: `Tenant: ${tenantName}`
              });
            }
            
            // Don't add "Projects" - just show the tenant
            
            this.updateContext({items: contextItems});
            return;
          }
          
          if (toolName && toolName.includes('get_project')) {
            // First, extract and cache entities from tool result
            this.extractAndCacheEntities(toolName, toolResult);
            
            const contextItems = [{label: 'Mia-Platform', icon: '🏢', tooltip: 'Mia-Platform Console'}];
            
            // Extract tenant ID from arguments
            const tenantId = toolArgs.tenantId || toolArgs.tenant_id;
            const projectId = toolArgs.projectId || toolArgs.project_id;
            
            if (tenantId) {
              // Try to get tenant name from cache
              const cachedTenant = this.getCachedEntity('tenant', tenantId);
              const tenantName = cachedTenant?.name || tenantId;
              
              contextItems.push({
                label: tenantName,
                icon: '🏛️',
                tooltip: `Tenant: ${tenantName}`
              });
            }
            
            if (projectId) {
              // Try to get project name from cache or tool result
              const cachedProject = this.getCachedEntity('project', projectId);
              let projectName = cachedProject?.name || projectId;
              
              // Also check tool result directly
              if (toolResult && typeof toolResult === 'object') {
                try {
                  const resultData = typeof toolResult === 'string' ? JSON.parse(toolResult) : toolResult;
                  if (resultData.name) {
                    projectName = resultData.name;
                  }
                } catch (e) {
                  // Ignore
                }
              }
              
              contextItems.push({
                label: projectName,
                icon: '📦',
                tooltip: `Project: ${projectName}`
              });
            }
            
            this.updateContext({items: contextItems});
            return;
          }

          // Atlassian tools
          if (toolName && toolName.toLowerCase().includes('issue')) {
            this.updateContext({
              items: [
                {label: 'Atlassian', icon: '🔷', tooltip: 'Atlassian'}
              ]
            });
            return;
          }

          if (toolName && toolName.includes('confluence')) {
            this.updateContext({
              items: [
                {label: 'Confluence', icon: '📚', tooltip: 'Atlassian Confluence'}
              ]
            });
            return;
          }
        }
      }
    }

    // Fall back to parsing text
    if (message.text) {
      // eslint-disable-next-line no-console
      devLog('🔍 ContextManager: Parsing text for context', message.text.substring(0, 100));
      const textContext = this.parseContextFromMessage(message.text);
      if (textContext) {
        // eslint-disable-next-line no-console
        devLog('🔍 ContextManager: Found context from text', textContext);
        this.updateContext(textContext);
      } else {
        // eslint-disable-next-line no-console
        devLog('🔍 ContextManager: No context found in text');
      }
    }
  }

  /**
   * Set context manually (for explicit context setting)
   * @param {Array} items - Array of context items
   */
  setContext(items) {
    this.updateContext({items});
  }
}

// Export singleton instance
const contextManager = new ContextManager();
export default contextManager;
